import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import {
  Card,
  Typography,
  Select,
  Tag,
  Row,
  Col,
  Popover,
  Button,
  DatePicker,
  Switch,
  Statistic,
  message,
} from "antd";
import { FileTextOutlined, SwapOutlined, LinkOutlined, EyeOutlined } from "@ant-design/icons";
import InvoiceViewModal from "../Shared/Modals/InvoiceViewModal";
import { useNavigate } from "react-router-dom";
import {
  getPartnerTransactions,
  getPartnerTransactionsPDF,
  getPartnerTransactionsExcel,
  getPartnerStatementPDF,
} from "../../api/overpayment";
import { getAllPartners, getVendorAgingReport } from "../../api/accounting";
import dayjs from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";
import { FileExcelOutlined } from "@ant-design/icons";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { secureExportToExcel } from "../../../../utils/secureExcelUtils";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
  invalidateCacheByPrefix,
} from "../../context/AccountingDataCache";

const { Title, Text } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

const formatCurrency = (value) => {
  const number = parseFloat(value);
  const dp = getDecimalPrecision();
  return isNaN(number)
    ? "-"
    : number.toLocaleString("en-US", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });
};

// Get global display currency settings
const getGlobalDisplayCurrency = () => {
  try {
    const currency = localStorage.getItem("global_display_currency") || "USD";
    const SYMBOLS = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "C$",
      AUD: "A$",
      CHF: "CHF",
      INR: "₹",
    };
    const symbol = SYMBOLS[currency] || currency;
    if (currency === "USD") return { currency, symbol, rate: 1 };
    const ratesStr = localStorage.getItem("custom_currency_rates");
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    return { currency, symbol, rate: rates[currency] || null };
  } catch (e) {
    return { currency: "USD", symbol: "$", rate: 1 };
  }
};

// Plain formatter for displaying stored amounts using GLOBAL display currency
const formatCurrencyPlain = (amount, originalCurrency) => {
  const value = parseFloat(amount || 0);
  const dp = getDecimalPrecision();
  if (Number.isNaN(value)) return `$ ${"0".padEnd(dp + 2, "0")}`;

  const {
    symbol: displaySymbol,
    rate,
    currency: displayCurrency,
  } = getGlobalDisplayCurrency();

  if (displayCurrency !== "USD" && rate && rate > 0) {
    const converted = value * rate;
    return `${displaySymbol} ${converted.toLocaleString("en-US", {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })}`;
  }

  return `$ ${value.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
};

const TransactionTypeBadge = ({ type, record }) => {
  const getColor = () => {
    switch (type) {
      case "transaction":
        return "blue";
      case "credit":
        return "purple";
      case "cash":
        return "green";
      case "invoice":
      case "bill":
        return "orange";
      default:
        return "default";
    }
  };

  const getLabel = () => {
    switch (type) {
      case "transaction":
        return "Bank Transaction";
      case "credit":
        return "Credit";
      case "cash":
        return "Cash Payment";
      case "invoice":
        return "Invoice";
      case "bill":
        return "Bill";
      default:
        return type;
    }
  };

  return <Tag color={getColor()}>{getLabel()}</Tag>;
};

const PartnerTransactions = ({ partnerId, selectedBrand }) => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(partnerId || null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTypes, setFilterTypes] = useState([]); // Changed to array for multi-select
  const [sortBy, setSortBy] = useState("chronological"); // chronological, invoices_first, transactions_first
  const [dateRange, setDateRange] = useState(null);
  const [partnerType, setPartnerType] = useState("customer");
  const [showCancelled, setShowCancelled] = useState(false); // Toggle for cancelled invoices
  const [showUnapproved, setShowUnapproved] = useState(false); // Toggle for unapproved invoices/bills
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const [viewInvoiceType, setViewInvoiceType] = useState("invoice");
  const [viewInvoiceVisible, setViewInvoiceVisible] = useState(false);

  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    exchangeRate,
    isConverted,
    decimalPrecision,
  } = useDisplayCurrency();

  // Listen for global brand/client changes to invalidate cache
  useEffect(() => {
    const handleGlobalBrandChanged = () => {
      // Invalidate partner transactions cache when brand changes
      invalidateCacheByPrefix("partner_transactions_");
    };

    const handleGlobalClientChanged = () => {
      // When client changes, brands will also change, so invalidate cache
      invalidateCacheByPrefix("partner_transactions_");
    };

    window.addEventListener("globalBrandChanged", handleGlobalBrandChanged);
    window.addEventListener("globalClientChanged", handleGlobalClientChanged);

    return () => {
      window.removeEventListener(
        "globalBrandChanged",
        handleGlobalBrandChanged
      );
      window.removeEventListener(
        "globalClientChanged",
        handleGlobalClientChanged
      );
    };
  }, []);

  // Grid API ref for accessing filtered data
  const gridApiRef = useRef(null);

  // Filter and sort persistence refs
  const savedFilterStateRef = useRef(null);
  const savedSortStateRef = useRef([
    { colId: "transaction_date", sort: "desc" },
  ]);
  const pendingFilterRestoreRef = useRef(false);
  const pendingSortRestoreRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(null);

  // Generate cache key based on all parameters that affect the data
  const cacheKey = useMemo(() => {
    if (!selectedPartner || !selectedBrand?.id) return null;
    const filterTypesKey =
      filterTypes.length > 0 ? filterTypes.sort().join(",") : "all";
    const dateRangeKey = dateRange
      ? `${dateRange[0]?.format("YYYY-MM-DD") || ""}_${
          dateRange[1]?.format("YYYY-MM-DD") || ""
        }`
      : "all";
    return `partner_transactions_${selectedBrand.id}_${selectedPartner}_${partnerType}_${filterTypesKey}_${sortBy}_${dateRangeKey}_${showCancelled}_${showUnapproved}`;
  }, [
    selectedPartner,
    selectedBrand?.id,
    partnerType,
    filterTypes,
    sortBy,
    dateRange,
    showCancelled,
    showUnapproved,
  ]);

  const fetchPartners = async () => {
    try {
      const response = await getAllPartners({
        brand: selectedBrand?.id,
        partner_type: partnerType,
      });
      setPartners(response.data);
      // if (response.data.length > 0) {
      //   setSelectedPartner(response.data[0].id);
      // }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  const fetchTransactions = useCallback(
    async (force = false) => {
      if (!selectedPartner || !cacheKey) return;

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData && lastFetchedCacheKeyRef.current === cacheKey) {
          // Data already loaded for this key
          return;
        }
        if (cachedData) {
          setTransactions(cachedData);
          lastFetchedCacheKeyRef.current = cacheKey;
          return;
        }
      }

      // Save current filter and sort state BEFORE refreshing
      if (gridApiRef.current) {
        try {
          const currentFilters = gridApiRef.current.getFilterModel();
          if (currentFilters && Object.keys(currentFilters).length > 0) {
            savedFilterStateRef.current = currentFilters;
            pendingFilterRestoreRef.current = true;
          }
          const columnState = gridApiRef.current.getColumnState();
          const sortState = columnState
            .filter((col) => col.sort !== null)
            .map((col) => ({
              colId: col.colId,
              sort: col.sort,
              sortIndex: col.sortIndex,
            }));
          if (sortState.length > 0) {
            savedSortStateRef.current = sortState;
            pendingSortRestoreRef.current = true;
          }
        } catch (e) {
          // Grid might not be ready, ignore
        }
      }

      setLoading(true);
      try {
        // Build API type from filterTypes array
        let apiType = null;
        if (filterTypes.length === 1) {
          // Single type selected
          if (filterTypes[0] === "invoice_bill") {
            apiType = "invoice";
          } else if (filterTypes[0] === "overpayment") {
            apiType = "overpayment";
          } else {
            apiType = filterTypes[0];
          }
        } else if (filterTypes.length > 1) {
          // Multiple types - backend will handle 'all'
          apiType = "all";
        } else {
          // No explicit filter chosen: ask backend for all types
          apiType = "all";
        }

        const apiSortBy = sortBy === "chronological" ? null : sortBy;
        const startDate = dateRange?.[0]?.format("YYYY-MM-DD") || null;
        const endDate = dateRange?.[1]?.format("YYYY-MM-DD") || null;

        const response = await getPartnerTransactions(
          selectedPartner,
          selectedBrand?.id,
          apiType,
          apiSortBy,
          startDate,
          endDate,
          partnerType,
          showUnapproved, // Pass unapproved parameter to backend
          showCancelled // Pass showCancelled parameter to backend
        );

        let fetchedTransactions = response.transactions || [];

        // Backend already handles approval and cancelled filtering based on parameters
        // When unapproved=false: backend only returns approved items
        // When unapproved=true: backend returns both approved and unapproved items
        // When show_cancelled=false: backend excludes cancelled invoices
        // When show_cancelled=true: backend includes cancelled invoices
        // No need for additional frontend filtering

        // Enforce partnerType constraint:
        // - customer: never show bills
        // - vendor:   never show invoices
        fetchedTransactions = fetchedTransactions.filter((item) => {
          if (partnerType === "customer") {
            // Hide bills for customers
            if (item.transaction_type === "bill") return false;
            // For credits, only keep invoice-type credits (filter out bill-type credits)
            if (item.transaction_type === "credit") {
              const creditType =
                item.credit_type || item.creditType || item.type;
              // Only show invoice-type credits for customers
              if (creditType === "bill" || creditType === "bill_credit") {
                return false;
              }
              // If credit_type is missing or is "invoice", show it
              return true;
            }
            return true;
          }
          if (partnerType === "vendor") {
            // Hide invoices for vendors
            if (item.transaction_type === "invoice") return false;
            // For credits, only keep bill-type credits (filter out invoice-type credits)
            if (item.transaction_type === "credit") {
              const creditType =
                item.credit_type || item.creditType || item.type;
              // Only show bill-type credits for vendors
              if (creditType === "invoice" || creditType === "invoice_credit") {
                return false;
              }
              // If credit_type is missing or is "bill", show it
              return true;
            }
            return true;
          }
          return true;
        });

        // Apply frontend multi-type filtering if multiple types selected
        if (filterTypes.length > 1 && !filterTypes.includes("all")) {
          fetchedTransactions = fetchedTransactions.filter((item) => {
            const type = item.transaction_type;
            // Map transaction_type to filterTypes values
            if (type === "transaction" && filterTypes.includes("transaction"))
              return true;
            if (type === "credit" && filterTypes.includes("credit"))
              return true;
            if (type === "cash" && filterTypes.includes("cash")) return true;
            if (type === "overpayment" && filterTypes.includes("overpayment"))
              return true;
            if (
              (type === "invoice" || type === "bill") &&
              filterTypes.includes("invoice_bill")
            )
              return true;
            return false;
          });
        }

        // Apply frontend sorting
        if (sortBy === "chronological") {
          // Sort by transaction_date in descending order (newest first)
          fetchedTransactions.sort((a, b) => {
            const dateA = dayjs(a.transaction_date);
            const dateB = dayjs(b.transaction_date);
            return dateB.diff(dateA); // Descending order
          });
        }

        setTransactions(fetchedTransactions);
        // Store in GLOBAL cache (5 minutes TTL)
        setCachedData(cacheKey, fetchedTransactions, CacheTTL.MEDIUM);
        lastFetchedCacheKeyRef.current = cacheKey;
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedPartner,
      selectedBrand,
      filterTypes,
      sortBy,
      dateRange,
      partnerType,
      showCancelled,
      showUnapproved,
      cacheKey,
    ]
  );

  // Preload exchange rates on component mount
  useEffect(() => {
    // preloadExchangeRates().catch((err) => {
    //   console.warn("Failed to preload exchange rates:", err);
    // });
  }, []);

  useEffect(() => {
    if (selectedBrand) {
      setSelectedPartner(null);
      fetchPartners();
    }
  }, [selectedBrand, partnerType]);

  useEffect(() => {
    if (selectedPartner) fetchTransactions();
    else setTransactions([]);
  }, [selectedPartner, fetchTransactions]);

  // Effect to restore filters and sort AFTER data has loaded
  useEffect(() => {
    const hasFiltersToRestore =
      pendingFilterRestoreRef.current &&
      savedFilterStateRef.current &&
      Object.keys(savedFilterStateRef.current).length > 0;
    const hasSortToRestore =
      pendingSortRestoreRef.current &&
      savedSortStateRef.current &&
      savedSortStateRef.current.length > 0;

    if (
      (hasFiltersToRestore || hasSortToRestore) &&
      !loading &&
      transactions.length > 0
    ) {
      const restoreGridState = (attempt = 1) => {
        setTimeout(() => {
          if (gridApiRef.current) {
            try {
              if (hasFiltersToRestore) {
                gridApiRef.current.setFilterModel(savedFilterStateRef.current);
                pendingFilterRestoreRef.current = false;
              }
              if (hasSortToRestore) {
                gridApiRef.current.applyColumnState({
                  state: savedSortStateRef.current,
                  defaultState: { sort: null },
                });
                pendingSortRestoreRef.current = false;
              }
            } catch (e) {
              if (attempt < 5) restoreGridState(attempt + 1);
              else {
                pendingFilterRestoreRef.current = false;
                pendingSortRestoreRef.current = false;
              }
            }
          } else if (attempt < 5) {
            restoreGridState(attempt + 1);
          } else {
            pendingFilterRestoreRef.current = false;
            pendingSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreGridState();
    }
  }, [loading, transactions.length]);

  const handlePartnerChange = (value) => setSelectedPartner(value);

  const getFieldValue = (record, fieldName) => {
    const formatCurrencyValue = (value) => {
      if (value === null || value === undefined) return "-";
      const num = parseFloat(value);
      return isNaN(num)
        ? "-"
        : num.toLocaleString("en-US", {
            minimumFractionDigits: decimalPrecision,
            maximumFractionDigits: decimalPrecision,
          });
    };

    if (record.isAllocation) {
      switch (fieldName) {
        case "reference":
          return `→ ${record.number || ""}`;
        case "amount":
          // use total_amount if available, otherwise allocated amount
          return formatCurrencyValue(
            record.total_amount || record.allocated_amount || 0
          );
        case "allocated_amount":
          return formatCurrencyValue(record.allocated_amount || 0);
        case "remaining_amount":
          return formatCurrencyValue(record.remaining_amount || 0);
        case "transaction_type":
          return record.type_display || "";
        case "transaction_date":
          return record.transaction_date
            ? dayjs(record.transaction_date).format("YYYY-MM-DD")
            : "";
        default:
          return record[fieldName] || "-";
      }
    }

    // For non-allocation rows
    switch (fieldName) {
      case "reference":
        // Check multiple possible fields for reference/number
        // For bank transactions, prioritize transaction_number (TXN-{id})
        if (record.transaction_type === "transaction") {
          return (
            record.transaction_number ||
            record.reference ||
            record.number ||
            record.check_or_slip ||
            `TXN-${record.id}` ||
            "-"
          );
        }
        // For other types, use standard fields
        return (
          record.reference ||
          record.number ||
          record.transaction_number ||
          record.check_or_slip ||
          record.invoice_number ||
          record.credit_number ||
          record.cash_number ||
          `TXN-${record.id}` ||
          "-"
        );
      case "description":
        return record.description || record.detail_display || "-";
      case "amount":
        return formatCurrencyValue(record.total_amount || record.amount || 0);
      case "allocated_amount":
        return formatCurrencyValue(
          record.allocated_amount || record.allocated_sum || 0
        );
      case "remaining_amount":
        return formatCurrencyValue(record.remaining_amount || 0);
      case "transaction_date":
        return record.transaction_date
          ? dayjs(record.transaction_date).format("YYYY-MM-DD")
          : "-";
      default:
        return record[fieldName] || "-";
    }
  };

  // Calculate statistics based on stored amounts (assumed already normalized, e.g. USD)
  const statistics = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        totalAmount: 0,
        totalAllocated: 0,
        totalRemaining: 0,
      };
    }

    const totalAmount = transactions.reduce((sum, item) => {
      const amount =
        parseFloat(
          item.transaction_type === "transaction"
            ? item.amount
            : item.total_amount || item.amount || 0
        ) || 0;
      return sum + amount;
    }, 0);

    const totalAllocated = transactions.reduce((sum, item) => {
      // Only count allocations FROM transactions (not TO invoices)
      // For invoices/bills, allocated_amount represents how much was allocated TO them (already counted in transactions)
      // For transactions/credits/cash/overpayments, allocated_amount represents how much was allocated FROM them
      if (
        item.transaction_type === "invoice" ||
        item.transaction_type === "bill"
      ) {
        // Skip invoices/bills - their allocated_amount is already counted in the transactions
        return sum;
      }

      // Count allocations FROM transactions, credits, cash, AND overpayments (if they're allocated)
      // Only count if the item actually has allocations (allocated_amount > 0)
      const allocated =
        parseFloat(item.allocated_amount || item.allocated_sum || 0) || 0;

      // Only count if there are actual allocations
      if (allocated <= 0) {
        return sum;
      }

      return sum + allocated;
    }, 0);

    const totalRemaining = transactions.reduce((sum, item) => {
      // Only count remaining amounts for invoices/bills (unpaid amounts)
      // Don't count remaining amounts for transactions or overpayments in the total remaining
      // Total Remaining should represent unpaid invoice/bill amounts
      if (
        item.transaction_type === "invoice" ||
        item.transaction_type === "bill"
      ) {
        const remaining = parseFloat(item.remaining_amount || 0) || 0;
        return sum + remaining;
      }
      return sum;
    }, 0);

    // Fix floating point precision issues - round very small values to 0
    const cleanValue = (val) =>
      Math.abs(val) < 0.001 ? 0 : Math.round(val * 1000) / 1000;

    return {
      totalAmount: cleanValue(totalAmount),
      totalAllocated: cleanValue(totalAllocated),
      totalRemaining: cleanValue(totalRemaining),
    };
  }, [transactions]);

  // AG Grid column definitions
  const columnDefs = useMemo(() => {
    const getFieldValueForGrid = (params, fieldName) => {
      const record = params.data;
      return getFieldValue(record, fieldName);
    };

    return [
      {
        headerName: "Date",
        field: "transaction_date",
        valueGetter: (params) => {
          const date = params.data?.transaction_date;
          if (date) {
            return dayjs(date).format("YYYY-MM-DD");
          }
          return getFieldValue(params.data, "transaction_date");
        },
        sort: "desc", // Default sort by date descending (newest first)
        width: 120,
      },
      {
        headerName: "Type",
        field: "transaction_type",
        cellRenderer: (params) => {
          const record = params.data;
          const type = record?.transaction_type;
          if (record?.isAllocation) {
            return (
              <span style={{ paddingLeft: "20px" }}>{record.type_display}</span>
            );
          }
          return <TransactionTypeBadge type={type} record={record} />;
        },
        width: 150,
      },
      {
        headerName: "Reference/Number",
        field: "reference",
        valueGetter: (params) => getFieldValueForGrid(params, "reference"),
        width: 180,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record || record.isAllocation) return params.value || "-";
          const type = record.transaction_type;
          const ref = params.value || "-";
          const txId = record.id;
          let targetUrl = null;
          if (type === "transaction") {
            targetUrl = `/accounting/transactions-allocations?transaction=${txId}&txnType=bank_statement`;
          } else if (type === "bill") {
            targetUrl = "/accounting/accounts-payable";
          } else if (type === "invoice") {
            targetUrl = "/accounting/accounts-receivable";
          } else if (type === "credit") {
            targetUrl = `/accounting/transactions-allocations?transaction=${txId}&txnType=credit`;
          } else if (type === "cash") {
            targetUrl = `/accounting/transactions-allocations?transaction=${txId}&txnType=cash`;
          } else if (type === "overpayment") {
            targetUrl = `/accounting/transactions-allocations?transaction=${txId}&txnType=overpayment`;
          }
          if (targetUrl) {
            return (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(targetUrl);
                }}
                style={{ color: "#1890ff", cursor: "pointer" }}
                title={`Go to ${type === "bill" ? "Accounts Payable" : type === "invoice" ? "Accounts Receivable" : "Transactions & Allocations"}`}
              >
                {ref}
              </a>
            );
          }
          return ref;
        },
      },
      {
        headerName: "Status",
        field: "status",
        cellRenderer: (params) => {
          const record = params.data;
          if (record?.isAllocation) return "-";

          const status = record?.status;

          // For invoices/bills, show invoice status (draft, paid, partial, etc.)
          if (
            record?.transaction_type === "invoice" ||
            record?.transaction_type === "bill"
          ) {
            if (!status) return "-";

            const statusConfig = {
              draft: { color: "default", text: "Draft" },
              sent: { color: "blue", text: "Sent" },
              paid: { color: "green", text: "Paid" },
              partial: { color: "orange", text: "Partial" },
              overdue: { color: "red", text: "Overdue" },
              cancelled: { color: "volcano", text: "CANCELLED" },
            };

            const config = statusConfig[status] || {
              color: "default",
              text: status.toUpperCase(),
            };
            return <Tag color={config.color}>{config.text}</Tag>;
          }

          // For transactions, cash, credit, and overpayment, show allocation status
          // Use the status field from backend if available, otherwise calculate it
          if (
            record?.transaction_type === "transaction" ||
            record?.transaction_type === "cash" ||
            record?.transaction_type === "credit" ||
            record?.transaction_type === "overpayment"
          ) {
            // First, try to use the status field from backend (if available)
            if (
              status &&
              (status === "fully_allocated" ||
                status === "partially_allocated" ||
                status === "unallocated")
            ) {
              const statusConfig = {
                fully_allocated: { color: "green", text: "Fully Allocated" },
                partially_allocated: {
                  color: "orange",
                  text: "Partially Allocated",
                },
                unallocated: { color: "default", text: "Unallocated" },
              };
              const config = statusConfig[status] || {
                color: "default",
                text: "Unallocated",
              };
              return <Tag color={config.color}>{config.text}</Tag>;
            }

            // Fallback: calculate status from allocated/remaining amounts
            const allocated = parseFloat(record.allocated_amount || 0);
            const remaining = parseFloat(record.remaining_amount || 0);

            // Check if fully allocated (remaining is 0 or very close to 0)
            if (remaining <= 0.01 && allocated > 0) {
              return <Tag color="green">Fully Allocated</Tag>;
            } else if (allocated > 0) {
              return <Tag color="orange">Partially Allocated</Tag>;
            } else {
              return <Tag color="default">Unallocated</Tag>;
            }
          }

          return "-";
        },
        width: 120,
      },
      {
        headerName: "Amount",
        field: "amount",
        valueGetter: (params) => {
          const record = params.data;
          if (record?.isAllocation) {
            return (
              parseFloat(record.total_amount || record.allocated_amount || 0) ||
              0
            );
          }
          return parseFloat(record?.total_amount || record?.amount || 0) || 0;
        },
        cellRenderer: (params) => {
          const record = params.data;
          const currency =
            record?.currency || record?.originalData?.currency || "$";
          // Get raw numeric value directly, not formatted string
          let rawAmount;
          if (record?.isAllocation) {
            rawAmount =
              parseFloat(record.total_amount || record.allocated_amount || 0) ||
              0;
          } else {
            rawAmount =
              parseFloat(record?.total_amount || record?.amount || 0) || 0;
          }
          return formatCurrencyPlain(rawAmount, currency);
        },
        cellStyle: { textAlign: "right" },
        width: 130,
      },
      {
        headerName: "Allocated",
        field: "allocated_amount",
        valueGetter: (params) => {
          const record = params.data;
          return (
            parseFloat(
              record?.allocated_amount || record?.allocated_sum || 0
            ) || 0
          );
        },
        cellRenderer: (params) => {
          const record = params.data;
          const currency =
            record?.currency || record?.originalData?.currency || "$";
          // Get raw numeric value directly
          const rawAllocated =
            parseFloat(
              record?.allocated_amount || record?.allocated_sum || 0
            ) || 0;
          return formatCurrencyPlain(rawAllocated, currency);
        },
        cellStyle: { textAlign: "right" },
        width: 130,
      },
      {
        headerName: "Remaining",
        field: "remaining_amount",
        valueGetter: (params) => {
          const record = params.data;
          return parseFloat(record?.remaining_amount || 0) || 0;
        },
        cellRenderer: (params) => {
          const record = params.data;
          const currency =
            record?.currency || record?.originalData?.currency || "$";
          // Get raw numeric value directly
          const rawRemaining = parseFloat(record?.remaining_amount || 0) || 0;
          return formatCurrencyPlain(rawRemaining, currency);
        },
        cellStyle: { textAlign: "right" },
        width: 130,
      },
      {
        headerName: "Details",
        field: "details",
        cellRenderer: (params) => {
          const record = params.data;
          if (record?.isAllocation) return null;

          const allocations = record?.allocations || [];
          const count = allocations.length;

          if (sortBy === "transactions_first") {
            return count > 0 ? (
              <Tag>
                {count} {count === 1 ? "allocation" : "allocations"}
              </Tag>
            ) : (
              "-"
            );
          }

          if (count === 0) return "-";

          // Determine if we're viewing an invoice/bill (which shows transaction allocations)
          // or a transaction/cash/credit/overpayment (which shows invoice/overpayment allocations)
          const isInvoiceView =
            record.transaction_type === "invoice" ||
            record.transaction_type === "bill";
          const isOverpaymentView = record.transaction_type === "overpayment";

          const content = (
            <div
              style={{
                maxWidth: "600px",
                maxHeight: "400px",
                overflow: "auto",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #ddd" }}>
                    {isInvoiceView ? (
                      // For invoices: show transaction details
                      <>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          Transaction #
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          Type
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          Reference
                        </th>
                        <th style={{ padding: "8px", textAlign: "right" }}>
                          Amount
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          Date
                        </th>
                      </>
                    ) : (
                      // For transactions/overpayments: show invoice/overpayment details
                      <>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          {isOverpaymentView ? "Target" : "Invoice #"}
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          {isOverpaymentView ? "Type" : "Issue Date"}
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          {isOverpaymentView ? "Status" : "Status"}
                        </th>
                        <th style={{ padding: "8px", textAlign: "right" }}>
                          Amount
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          {isOverpaymentView ? "Reference" : "From Transaction"}
                        </th>
                        <th style={{ padding: "8px", textAlign: "left" }}>
                          Date
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((alloc) => {
                    if (isInvoiceView) {
                      // For invoices: show which transactions allocated to this invoice
                      const transactionNumber =
                        alloc.transaction_number ||
                        alloc.credit_number ||
                        alloc.cash_number ||
                        alloc.overpayment_number ||
                        "-";
                      const transactionRef = alloc.transaction_reference || "-";
                      const transactionType = alloc.transaction_number
                        ? "Bank Transaction"
                        : alloc.credit_number
                        ? "Credit"
                        : alloc.cash_number
                        ? "Cash"
                        : alloc.overpayment_number
                        ? "Overpayment"
                        : "-";

                      let allocTxnId = alloc.source_transaction_id || alloc.source_credit_id || alloc.source_cash_id || null;
                      let allocTxnType = alloc.source_transaction_id
                        ? "bank_statement"
                        : alloc.source_credit_id
                        ? "credit"
                        : alloc.source_cash_id
                        ? "cash"
                        : null;

                      return (
                        <tr
                          key={alloc.id}
                          style={{ borderBottom: "1px solid #eee" }}
                        >
                          <td style={{ padding: "8px" }}>
                            <div>
                              <div style={{ fontWeight: "500" }}>
                                {allocTxnId && allocTxnType ? (
                                  <a
                                    href={`/accounting/transactions-allocations?transaction=${allocTxnId}&txnType=${allocTxnType}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(`/accounting/transactions-allocations?transaction=${allocTxnId}&txnType=${allocTxnType}`);
                                    }}
                                    style={{ color: "#1890ff", cursor: "pointer" }}
                                    title="Open allocation modal"
                                  >
                                    {transactionNumber}
                                  </a>
                                ) : (
                                  transactionNumber
                                )}
                              </div>
                              {transactionRef && transactionRef !== "-" && (
                                <div
                                  style={{ fontSize: "11px", color: "#666" }}
                                >
                                  {transactionRef}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: "8px" }}>{transactionType}</td>
                          <td style={{ padding: "8px" }}>
                            {transactionRef !== "-" ? transactionRef : "-"}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            {(() => {
                              const allocCurrency =
                                alloc.currency || record.currency || "$";
                              const allocAmount =
                                parseFloat(alloc.amount || 0) || 0;
                              return `${allocCurrency} ${allocAmount.toFixed(
                                decimalPrecision
                              )}`;
                            })()}
                          </td>
                          <td style={{ padding: "8px" }}>
                            {alloc.allocation_date
                              ? dayjs(alloc.allocation_date).format(
                                  "YYYY-MM-DD"
                                )
                              : "-"}
                          </td>
                        </tr>
                      );
                    } else {
                      // For transactions/overpayments: show which invoices or other overpayments were allocated
                      // Check if this is an overpayment-to-overpayment allocation (only for overpayment records)
                      const isOverpaymentToOverpayment =
                        record.transaction_type === "overpayment" &&
                        (alloc.allocation_type ===
                          "overpayment_to_overpayment" ||
                          alloc.target_overpayment_id);

                      if (isOverpaymentToOverpayment) {
                        // Show overpayment-to-overpayment allocation (only for overpayment records)
                        const targetOverpaymentNumber =
                          alloc.overpayment_number ||
                          (alloc.target_overpayment_id
                            ? `OP-${alloc.target_overpayment_id}`
                            : "-");
                        const transactionRef =
                          alloc.transaction_reference || "-";

                        return (
                          <tr
                            key={alloc.id}
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            <td style={{ padding: "8px" }}>
                              <span
                                style={{ fontWeight: "500", color: "#1890ff" }}
                              >
                                Overpayment {targetOverpaymentNumber}
                              </span>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Tag color="blue">Overpayment</Tag>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Tag color="blue">Allocated</Tag>
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              {(() => {
                                const allocCurrency =
                                  alloc.currency || record.currency || "$";
                                const allocAmount =
                                  parseFloat(alloc.amount || 0) || 0;
                                return `${allocCurrency} ${allocAmount.toFixed(
                                  decimalPrecision
                                )}`;
                              })()}
                            </td>
                            <td style={{ padding: "8px" }}>
                              {transactionRef !== "-" ? transactionRef : "-"}
                            </td>
                            <td style={{ padding: "8px" }}>
                              {alloc.allocation_date
                                ? dayjs(alloc.allocation_date).format(
                                    "YYYY-MM-DD"
                                  )
                                : "-"}
                            </td>
                          </tr>
                        );
                      } else {
                        // Show invoice allocation
                        const transactionNumber =
                          alloc.transaction_number ||
                          alloc.credit_number ||
                          alloc.cash_number ||
                          alloc.overpayment_number ||
                          "-";
                        const transactionRef =
                          alloc.transaction_reference || "-";

                        return (
                          <tr
                            key={alloc.id}
                            style={{ borderBottom: "1px solid #eee" }}
                          >
                            <td style={{ padding: "8px" }}>
                              {(() => {
                                const invoiceNum = alloc.invoice?.invoice_number || alloc.invoice?.number;
                                const invoiceId = alloc.invoice?.id || alloc.invoice_id;
                                if (invoiceNum && invoiceId) {
                                  const invType = alloc.invoice?.invoice_type || (record.transaction_type === "bill" ? "bill" : "invoice");
                                  return (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setViewInvoiceId(invoiceId);
                                        setViewInvoiceType(invType);
                                        setViewInvoiceVisible(true);
                                      }}
                                      style={{ color: "#1890ff", cursor: "pointer" }}
                                    >
                                      {invoiceNum}
                                    </a>
                                  );
                                }
                                return invoiceNum || "-";
                              })()}
                            </td>
                            <td style={{ padding: "8px" }}>
                              {alloc.invoice?.issue_date
                                ? dayjs(alloc.invoice.issue_date).format(
                                    "YYYY-MM-DD"
                                  )
                                : "-"}
                            </td>
                            <td style={{ padding: "8px" }}>
                              {alloc.invoice?.status_display ||
                                alloc.invoice?.status ||
                                "-"}
                            </td>
                            <td style={{ padding: "8px", textAlign: "right" }}>
                              {(() => {
                                const allocCurrency =
                                  alloc.currency || record.currency || "$";
                                const allocAmount =
                                  parseFloat(alloc.amount || 0) || 0;
                                return `${allocCurrency} ${allocAmount.toFixed(
                                  decimalPrecision
                                )}`;
                              })()}
                            </td>
                            <td style={{ padding: "8px" }}>
                              <div>
                                <div style={{ fontWeight: "500" }}>
                                  {transactionNumber}
                                </div>
                                {transactionRef && transactionRef !== "-" && (
                                  <div
                                    style={{ fontSize: "11px", color: "#666" }}
                                  >
                                    {transactionRef}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "8px" }}>
                              {alloc.allocation_date
                                ? dayjs(alloc.allocation_date).format(
                                    "YYYY-MM-DD"
                                  )
                                : "-"}
                            </td>
                          </tr>
                        );
                      }
                    }
                  })}
                </tbody>
              </table>
            </div>
          );

          return (
            <Popover
              content={content}
              title={`${count} Allocation${count > 1 ? "s" : ""}`}
              trigger="click"
            >
              <Button size="small" icon={<FileTextOutlined />}>
                {count}
              </Button>
            </Popover>
          );
        },
        width: 100,
      },
    ];
  }, [sortBy]);

  // Default column definition for AG Grid
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Export to PDF with allocation details - respects AG Grid column filters
  const exportToPDFWithAllocations = useCallback(async () => {
    // Get filtered data from AG Grid if available, otherwise use transactions state
    let dataToExport = [];

    if (gridApiRef.current) {
      // Use AG Grid's filtered/sorted data
      gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
        if (node.data && !node.data.isAllocation) {
          dataToExport.push(node.data);
        }
      });
    } else {
      // Fallback to transactions state
      dataToExport = transactions;
    }

    if (!dataToExport || dataToExport.length === 0) {
      message.warning("No transactions to export (check your filters)");
      return;
    }

    const doc = new jsPDF("landscape");
    const partner = partners.find((p) => p.id === selectedPartner);
    const partnerName = partner?.name || `Partner ${selectedPartner}`;

    // Title
    doc.setFontSize(14);
    doc.text(`Partner Statement - ${partnerName}`, 14, 10);

    // Date range info
    doc.setFontSize(9);
    let dateRangeText = "All dates";
    if (dateRange && dateRange.length === 2) {
      dateRangeText = `${dateRange[0].format(
        "YYYY-MM-DD"
      )} to ${dateRange[1].format("YYYY-MM-DD")}`;
    }
    doc.text(`Date Range: ${dateRangeText}`, 14, 17);
    doc.text(`Statement Date: ${dayjs().format("YYYY-MM-DD")}`, 14, 22);

    // Separate invoices/bills from transactions
    const invoicesBills = dataToExport.filter(
      (t) => t.transaction_type === "invoice" || t.transaction_type === "bill"
    );
    const otherTransactions = dataToExport.filter(
      (t) => t.transaction_type !== "invoice" && t.transaction_type !== "bill"
    );

    // Group transactions by invoice/bill they're allocated to
    const transactionGroups = new Map();
    otherTransactions.forEach((transaction) => {
      if (transaction.allocations && transaction.allocations.length > 0) {
        transaction.allocations.forEach((alloc) => {
          const invoiceId = alloc.invoice?.id || alloc.invoice_id;
          if (invoiceId) {
            if (!transactionGroups.has(invoiceId)) {
              transactionGroups.set(invoiceId, []);
            }
            transactionGroups.get(invoiceId).push({
              ...transaction,
              allocation: alloc,
            });
          }
        });
      }
    });

    // Prepare table data: invoices/bills first, then their allocated transactions
    const tableData = [];
    const invoiceRowIndices = []; // Track which rows are invoices/bills
    const transactionRowIndices = []; // Track which rows are transactions
    let startY = 28;

    // Add invoices/bills with their allocated transactions
    invoicesBills.forEach((invoice) => {
      const ref = getFieldValue(invoice, "reference");
      const date = getFieldValue(invoice, "transaction_date");
      const type = invoice.transaction_type || "-";
      const statusDisplay = invoice.status || "-";

      // CRITICAL: Get RAW numeric values directly from record to avoid parseFloat issues with comma-formatted strings
      const invoiceCurrency =
        invoice.currency || invoice.originalData?.currency || "$";
      const amountNum =
        parseFloat(invoice.total_amount || invoice.amount || 0) || 0;
      const allocatedNum =
        parseFloat(invoice.allocated_amount || invoice.allocated_sum || 0) || 0;
      const remainingNum = parseFloat(invoice.remaining_amount || 0) || 0;
      const amountDisplay = `${invoiceCurrency} ${amountNum.toLocaleString(
        "en-US",
        { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
      )}`;
      const allocatedDisplay = `${invoiceCurrency} ${allocatedNum.toLocaleString(
        "en-US",
        { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
      )}`;
      const remainingDisplay = `${invoiceCurrency} ${remainingNum.toLocaleString(
        "en-US",
        { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
      )}`;

      // Invoice/Bill row (bold/highlighted)
      const invoiceRowIndex = tableData.length;
      invoiceRowIndices.push(invoiceRowIndex);
      tableData.push([
        date,
        type.toUpperCase(),
        ref,
        statusDisplay,
        amountDisplay,
        allocatedDisplay,
        remainingDisplay,
        "",
      ]);

      // Get transactions allocated to this invoice
      const allocatedTransactions = transactionGroups.get(invoice.id) || [];

      // Also check allocations from the invoice itself
      if (invoice.allocations && invoice.allocations.length > 0) {
        invoice.allocations.forEach((alloc) => {
          const transactionNumber =
            alloc.transaction_number ||
            alloc.credit_number ||
            alloc.cash_number ||
            alloc.overpayment_number ||
            "-";
          const transactionRef = alloc.transaction_reference || "-";
          const transactionType = alloc.transaction_number
            ? "Bank Transaction"
            : alloc.credit_number
            ? "Credit"
            : alloc.cash_number
            ? "Cash"
            : alloc.overpayment_number
            ? "Overpayment"
            : "-";
          const allocAmount = parseFloat(alloc.amount || 0).toLocaleString(
            "en-US",
            { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
          );
          const allocDate = alloc.allocation_date
            ? dayjs(alloc.allocation_date).format("YYYY-MM-DD")
            : "-";

          // Transaction row (indented visually)
          const transactionRowIndex = tableData.length;
          transactionRowIndices.push(transactionRowIndex);
          tableData.push([
            allocDate,
            `  ${transactionType}`, // Indent with spaces
            `  ${transactionNumber}${
              transactionRef !== "-" ? ` (${transactionRef})` : ""
            }`,
            "Allocated",
            `$${allocAmount}`,
            `$${allocAmount}`,
            "$0.00",
            `Allocated to ${ref}`,
          ]);
        });
      }

      // Add transactions from the grouped map
      allocatedTransactions.forEach((transactionWithAlloc) => {
        const transactionNumber =
          transactionWithAlloc.transaction_number ||
          transactionWithAlloc.credit_number ||
          transactionWithAlloc.cash_number ||
          transactionWithAlloc.overpayment_number ||
          `TXN-${transactionWithAlloc.id}`;
        const transactionRef =
          transactionWithAlloc.reference ||
          transactionWithAlloc.check_or_slip ||
          "-";
        const transactionType =
          transactionWithAlloc.transaction_type === "transaction"
            ? "Bank Transaction"
            : transactionWithAlloc.transaction_type === "credit"
            ? "Credit"
            : transactionWithAlloc.transaction_type === "cash"
            ? "Cash"
            : transactionWithAlloc.transaction_type === "overpayment"
            ? "Overpayment"
            : "-";
        const allocAmount = parseFloat(
          transactionWithAlloc.allocation?.amount || 0
        ).toLocaleString("en-US", {
          minimumFractionDigits: decimalPrecision,
          maximumFractionDigits: decimalPrecision,
        });
        const allocDate = transactionWithAlloc.allocation?.allocation_date
          ? dayjs(transactionWithAlloc.allocation.allocation_date).format(
              "YYYY-MM-DD"
            )
          : transactionWithAlloc.transaction_date
          ? dayjs(transactionWithAlloc.transaction_date).format("YYYY-MM-DD")
          : "-";

        // Transaction row (indented visually)
        const transactionRowIndex = tableData.length;
        transactionRowIndices.push(transactionRowIndex);
        tableData.push([
          allocDate,
          `  ${transactionType}`, // Indent with spaces
          `  ${transactionNumber}${
            transactionRef !== "-" ? ` (${transactionRef})` : ""
          }`,
          "Allocated",
          `$${allocAmount}`,
          `$${allocAmount}`,
          "$0.00",
          `Allocated to ${ref}`,
        ]);
      });
    });

    // Add unallocated transactions (transactions not allocated to any invoice)
    const unallocatedTransactions = otherTransactions.filter((transaction) => {
      if (!transaction.allocations || transaction.allocations.length === 0) {
        return true;
      }
      // Check if all allocations are to invoices we've already processed
      return transaction.allocations.some((alloc) => {
        const invoiceId = alloc.invoice?.id || alloc.invoice_id;
        return !invoicesBills.find((inv) => inv.id === invoiceId);
      });
    });

    if (unallocatedTransactions.length > 0) {
      // Add separator
      tableData.push([
        "",
        "",
        "--- Unallocated Transactions ---",
        "",
        "",
        "",
        "",
        "",
      ]);

      unallocatedTransactions.forEach((transaction) => {
        const ref = getFieldValue(transaction, "reference");
        const date = getFieldValue(transaction, "transaction_date");
        const type =
          transaction.transaction_type === "transaction"
            ? "Bank Transaction"
            : transaction.transaction_type === "credit"
            ? "Credit"
            : transaction.transaction_type === "cash"
            ? "Cash"
            : transaction.transaction_type === "overpayment"
            ? "Overpayment"
            : "-";
        const allocated = parseFloat(transaction.allocated_amount || 0);
        const remaining = parseFloat(transaction.remaining_amount || 0);
        const total = parseFloat(
          transaction.amount || transaction.total_amount || 0
        );
        const transactionCurrency =
          transaction.currency || transaction.originalData?.currency || "$";
        const totalDisplay = `${transactionCurrency} ${total.toLocaleString(
          "en-US",
          { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
        )}`;
        const allocatedDisplay = `${transactionCurrency} ${allocated.toLocaleString(
          "en-US",
          { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
        )}`;
        const remainingDisplay = `${transactionCurrency} ${remaining.toLocaleString(
          "en-US",
          { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
        )}`;

        let statusDisplay = "Unallocated";
        if (remaining <= 0.01 && allocated > 0) {
          statusDisplay = "Fully Allocated";
        } else if (allocated > 0) {
          statusDisplay = "Partially Allocated";
        }

        tableData.push([
          date,
          type,
          ref,
          statusDisplay,
          totalDisplay,
          allocatedDisplay,
          remainingDisplay,
          "-",
        ]);
      });
    }

    // Calculate proper column widths for landscape
    const usableWidth = 269;
    const columnWidths = {
      date: 30,
      type: 35,
      reference: 55,
      status: 28,
      amount: 30,
      allocated: 30,
      remaining: 30,
      details: 31,
    };

    // Add main transactions table
    autoTable(doc, {
      head: [
        [
          "Date",
          "Type",
          "Reference/Number",
          "Status",
          "Amount",
          "Allocated",
          "Remaining",
          "Details",
        ],
      ],
      body: tableData,
      startY: startY,
      theme: "grid",
      headStyles: {
        fillColor: [33, 37, 41],
        fontSize: 8,
        textColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: {
          cellWidth: columnWidths.date,
          halign: "center",
          valign: "middle",
        },
        1: {
          cellWidth: columnWidths.type,
          halign: "center",
          valign: "middle",
        },
        2: {
          cellWidth: columnWidths.reference,
          halign: "left",
          valign: "middle",
        },
        3: {
          cellWidth: columnWidths.status,
          halign: "center",
          valign: "middle",
        },
        4: {
          cellWidth: columnWidths.amount,
          halign: "right",
          valign: "middle",
        },
        5: {
          cellWidth: columnWidths.allocated,
          halign: "right",
          valign: "middle",
        },
        6: {
          cellWidth: columnWidths.remaining,
          halign: "right",
          valign: "middle",
        },
        7: {
          cellWidth: columnWidths.details,
          halign: "left",
          valign: "middle",
          fontSize: 6,
        },
      },
      margin: { top: startY, left: 14, right: 14, bottom: 20 },
      pageBreak: "auto",
      showHead: "everyPage",
      tableWidth: "auto",
      horizontalPageBreak: false,
      didParseCell: (data) => {
        // Style invoice/bill rows with bold text and darker background
        if (invoiceRowIndices.includes(data.row.index)) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 220, 220]; // Medium gray background
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fontSize = 9;
        }
        // Style transaction rows with lighter background and smaller font
        if (transactionRowIndices.includes(data.row.index)) {
          data.cell.styles.fillColor = [250, 250, 250]; // Very light gray background
          data.cell.styles.textColor = [60, 60, 60];
          data.cell.styles.fontSize = 7;
        }
      },
      didDrawPage: (data) => {
        // Update startY for next section
        startY = data.cursor.y + 10;
      },
    });

    // Fetch and add aging report
    try {
      if (selectedBrand && selectedPartner) {
        const agingResponse = await getVendorAgingReport(
          [selectedBrand.id],
          {}
        );

        // The API returns { vendors: [...], bucket_totals: {...}, grand_total: ... }
        // Find the aging data for this specific partner from the vendors array
        const aging = agingResponse.vendors?.find(
          (vendor) =>
            vendor.vendor_id === selectedPartner &&
            vendor.brand_id === selectedBrand.id
        );

        if (aging) {
          const finalY = doc.lastAutoTable.finalY || startY;

          // Add spacing before aging report
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("Aging Summary", 14, finalY + 20);
          doc.setFont("helvetica", "normal");

          // Format aging table to match backend format exactly
          // Columns: Current Due, 1-30 Days Past Due, 31-60 Days Past Due, 61-90 Days Past Due, 90+ Days Past Due, Amount Due
          const agingTableData = [
            [
              `$${Math.abs(aging.current || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
              `$${Math.abs(aging.past_1_30 || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
              `$${Math.abs(aging.past_30_60 || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
              `$${Math.abs(aging.past_60_90 || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
              `$${Math.abs(aging.past_90_plus || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
              `$${Math.abs(aging.total || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}`,
            ],
          ];

          autoTable(doc, {
            head: [
              [
                "Current Due",
                "1-30 Days Past Due",
                "31-60 Days Past Due",
                "61-90 Days Past Due",
                "90+ Days Past Due",
                "Amount Due",
              ],
            ],
            body: agingTableData,
            startY: finalY + 25,
            theme: "grid",
            headStyles: {
              fillColor: [33, 37, 41],
              fontSize: 9,
              textColor: [255, 255, 255],
              halign: "center",
              fontStyle: "bold",
            },
            styles: {
              fontSize: 9,
              cellPadding: 3,
              halign: "right",
            },
            columnStyles: {
              0: { cellWidth: 45, halign: "right" }, // Current Due
              1: { cellWidth: 50, halign: "right" }, // 1-30 Days Past Due
              2: { cellWidth: 50, halign: "right" }, // 31-60 Days Past Due
              3: { cellWidth: 50, halign: "right" }, // 61-90 Days Past Due
              4: { cellWidth: 45, halign: "right" }, // 90+ Days Past Due
              5: { cellWidth: 50, halign: "right", fontStyle: "bold" }, // Amount Due (bold)
            },
            margin: { left: 14, right: 14 },
            didParseCell: (data) => {
              // Style the Amount Due column (last column) with bold
              if (data.column.index === 5) {
                data.cell.styles.fontStyle = "bold";
              }
            },
          });
        }
      }
    } catch (error) {
      console.error("Error fetching aging report:", error);
      // Continue without aging report if it fails
    }

    // Save PDF
    const fileName = `partner_statement_${selectedPartner}_${dayjs().format(
      "YYYYMMDD"
    )}.pdf`;
    doc.save(fileName);
  }, [
    transactions,
    partners,
    selectedPartner,
    selectedBrand,
    partnerType,
    dateRange,
    getFieldValue,
  ]);

  // Export to Excel with allocation details - respects AG Grid column filters
  const exportToExcelWithAllocations = useCallback(async () => {
    // Get filtered data from AG Grid if available, otherwise use transactions state
    let dataToExport = [];

    if (gridApiRef.current) {
      // Use AG Grid's filtered/sorted data
      gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
        if (node.data && !node.data.isAllocation) {
          dataToExport.push(node.data);
        }
      });
    } else {
      // Fallback to transactions state
      dataToExport = transactions;
    }

    if (!dataToExport || dataToExport.length === 0) {
      message.warning("No transactions to export (check your filters)");
      return;
    }

    const partner = partners.find((p) => p.id === selectedPartner);
    const partnerName = partner?.name || `Partner ${selectedPartner}`;

    // Prepare Excel data
    const excelData = [];

    // Add header row
    excelData.push([
      "Date",
      "Type",
      "Reference/Number",
      "Status",
      "Amount",
      "Allocated",
      "Remaining",
      "Allocation Details",
    ]);

    // Add transaction rows with allocation details
    dataToExport.forEach((transaction) => {
      const ref = getFieldValue(transaction, "reference");
      const date = getFieldValue(transaction, "transaction_date");
      const type = transaction.transaction_type || "-";

      // Calculate status for transactions (Fully Allocated, Partially Allocated, or invoice status)
      let statusDisplay = "-";
      if (
        transaction.transaction_type === "invoice" ||
        transaction.transaction_type === "bill"
      ) {
        statusDisplay = transaction.status || "-";
      } else {
        // For transactions (bank, cash, credit), show allocation status
        const allocated = parseFloat(transaction.allocated_amount || 0);
        const remaining = parseFloat(transaction.remaining_amount || 0);

        if (remaining <= 0.01 && allocated > 0) {
          statusDisplay = "Fully Allocated";
        } else if (allocated > 0) {
          statusDisplay = "Partially Allocated";
        } else {
          statusDisplay = "Unallocated";
        }
      }

      // Build allocation details string (organized format)
      // For invoices: show transaction details
      // For transactions: show invoice details with transaction info
      let allocationDetails = "";
      if (transaction.allocations && transaction.allocations.length > 0) {
        const isInvoice =
          transaction.transaction_type === "invoice" ||
          transaction.transaction_type === "bill";

        const allocDetailsArray = transaction.allocations.map((alloc) => {
          const allocAmount = parseFloat(alloc.amount || 0);
          const allocCurrency = alloc.currency || transaction.currency || "$";
          const allocAmountDisplay = `${allocCurrency} ${allocAmount.toFixed(
            decimalPrecision
          )}`;
          const allocDate = alloc.allocation_date
            ? dayjs(alloc.allocation_date).format("YYYY-MM-DD")
            : "-";

          // Get transaction number/reference from allocation
          const transactionNumber =
            alloc.transaction_number ||
            alloc.credit_number ||
            alloc.cash_number ||
            alloc.overpayment_number ||
            "-";
          const transactionRef = alloc.transaction_reference || "-";
          const transactionType = alloc.transaction_number
            ? "Bank Transaction"
            : alloc.credit_number
            ? "Credit"
            : alloc.cash_number
            ? "Cash"
            : alloc.overpayment_number
            ? "Overpayment"
            : "-";

          if (isInvoice) {
            // For invoices: show which transactions allocated to this invoice
            // Format: TXN-XXX (Type) | Reference | Amount | Date
            return `${transactionNumber} (${transactionType})${
              transactionRef !== "-" ? ` | ${transactionRef}` : ""
            } | Amount: ${allocAmountDisplay} | Allocated: ${allocDate}`;
          } else {
            // For transactions: show which invoices were allocated
            const allocInvoiceNum =
              alloc.invoice?.invoice_number ||
              alloc.invoice?.number ||
              alloc.invoice_number ||
              "-";
            const allocIssueDate = alloc.invoice?.issue_date
              ? dayjs(alloc.invoice.issue_date).format("YYYY-MM-DD")
              : "-";
            const allocStatus =
              alloc.invoice?.status_display || alloc.invoice?.status || "-";

            // Organized format: Invoice # | Issue Date | Status | Amount | From: TXN-XXX | Allocated: Date
            const fromTransaction =
              transactionNumber !== "-"
                ? `From: ${transactionNumber}${
                    transactionRef !== "-" ? ` (${transactionRef})` : ""
                  }`
                : "";
            return `Invoice #${allocInvoiceNum} | Issue: ${allocIssueDate} | Status: ${allocStatus} | Amount: ${allocAmountDisplay}${
              fromTransaction ? ` | ${fromTransaction}` : ""
            } | Allocated: ${allocDate}`;
          }
        });
        allocationDetails = allocDetailsArray.join("; ");
      }

      // CRITICAL: Get RAW numeric values directly from record to avoid parseFloat issues with comma-formatted strings
      const currency =
        transaction.currency || transaction.originalData?.currency || "$";
      const amountNum =
        parseFloat(transaction.total_amount || transaction.amount || 0) || 0;
      const allocatedNum =
        parseFloat(
          transaction.allocated_amount || transaction.allocated_sum || 0
        ) || 0;
      const remainingNum = parseFloat(transaction.remaining_amount || 0) || 0;
      const amountDisplay = `${currency} ${amountNum.toLocaleString("en-US", {
        minimumFractionDigits: decimalPrecision,
        maximumFractionDigits: decimalPrecision,
      })}`;
      const allocatedDisplay = `${currency} ${allocatedNum.toLocaleString(
        "en-US",
        { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
      )}`;
      const remainingDisplay = `${currency} ${remainingNum.toLocaleString(
        "en-US",
        { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
      )}`;

      // Main transaction row
      excelData.push([
        date,
        type,
        ref,
        statusDisplay,
        amountDisplay,
        allocatedDisplay,
        remainingDisplay,
        allocationDetails,
      ]);
    });

    // Create workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    worksheet["!cols"] = [
      { wch: 12 }, // Date
      { wch: 15 }, // Type
      { wch: 25 }, // Reference/Number
      { wch: 15 }, // Status
      { wch: 12 }, // Amount
      { wch: 12 }, // Allocated
      { wch: 12 }, // Remaining
      { wch: 100 }, // Allocation Details
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

    // Save file
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    const fileName = `partner_statement_${selectedPartner}_${timestamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [transactions, partners, selectedPartner, getFieldValue]);

  return (
    <Card>
      {/* ROW 1 - Filters */}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5}>Partner Transactions</Title>
        </Col>

        {/* ROW 1 - Filters with labels */}
        <Col span={6}>
          <div>
            <Text strong>Partner Type</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={partnerType}
              onChange={(value) => setPartnerType(value)}
              options={[
                { value: "customer", label: "Customer" },
                { value: "vendor", label: "Vendor" },
              ]}
            />
          </div>
        </Col>

        <Col span={6}>
          <div>
            <Text strong>Select Partner</Text>
            <Select
              placeholder="Select Partner"
              style={{ width: "100%", marginTop: 4 }}
              value={selectedPartner}
              onChange={handlePartnerChange}
              optionFilterProp="children"
              showSearch
              allowClear
            >
              <Select.Option value={null} disabled>
                -- Select a Partner --
              </Select.Option>
              {partners
                .filter((p) => p.partner_type === partnerType)
                .map((partner) => (
                  <Select.Option key={partner.id} value={partner.id}>
                    {partner.name}
                  </Select.Option>
                ))}
            </Select>
          </div>
        </Col>

        <Col span={6}>
          <div>
            <Text strong>Filter by Type (Multi-select)</Text>
            <Select
              mode="multiple"
              style={{ width: "100%", marginTop: 4 }}
              value={filterTypes}
              onChange={setFilterTypes}
              placeholder="Select types to filter"
              allowClear
              options={[
                { value: "transaction", label: "Bank Transactions" },
                { value: "credit", label: "Credits" },
                { value: "cash", label: "Cash Payments" },
                { value: "overpayment", label: "Overpayments" },
                { value: "invoice_bill", label: "Invoices/Bills" },
              ]}
            />
          </div>
        </Col>

        <Col span={6}>
          <div>
            <Text strong>Sort By</Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: "chronological", label: "Chronological Order" },
                { value: "invoices_first", label: "Invoices First" },
                { value: "transactions_first", label: "Transactions First" },
              ]}
            />
          </div>
        </Col>

        {/* ROW 2 - Date and PDF download */}
        <Col span={6}>
          <div>
            <Text strong>Date Range</Text>
            <DatePicker.RangePicker
              style={{ width: "100%", marginTop: 4 }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              format="YYYY-MM-DD"
              allowClear
            />
          </div>
        </Col>

        <Col span={6}>
          <div>
            <Text strong>Show Cancelled Invoices</Text>
            <div style={{ marginTop: 4 }}>
              <Switch
                checked={showCancelled}
                onChange={setShowCancelled}
                checkedChildren="Show"
                unCheckedChildren="Hide"
              />
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {showCancelled ? "Showing cancelled" : "Hiding cancelled"}
              </Text>
            </div>
          </div>
        </Col>

        <Col span={6}>
          <div>
            <Text strong>Include Unapproved Items</Text>
            <div style={{ marginTop: 4 }}>
              <Switch
                checked={showUnapproved}
                onChange={setShowUnapproved}
                checkedChildren="Include"
                unCheckedChildren="Exclude"
              />
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                {showUnapproved
                  ? "Showing all (approved + unapproved)"
                  : "Showing approved only"}
              </Text>
            </div>
          </div>
        </Col>

        <Col span={3}>
          <div>
            <Text strong>Download PDF</Text>
            <Button
              icon={<DownloadOutlined />}
              type="primary"
              style={{ width: "100%", marginTop: 4 }}
              disabled={!selectedPartner || transactions.length === 0}
              onClick={async () => {
                try {
                  setLoading(true);
                  // Use backend ReportLab endpoint
                  const blob = await getPartnerStatementPDF(
                    selectedPartner,
                    selectedBrand?.id || null,
                    partnerType,
                    dateRange && dateRange.length === 2
                      ? dateRange[0].format("YYYY-MM-DD")
                      : null,
                    dateRange && dateRange.length === 2
                      ? dateRange[1].format("YYYY-MM-DD")
                      : null,
                    showCancelled, // Pass showCancelled toggle to backend
                    showUnapproved // Pass showUnapproved toggle to backend
                  );

                  const url = window.URL.createObjectURL(
                    new Blob([blob], { type: "application/pdf" })
                  );
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `partner_statement_${selectedPartner}_${dayjs().format(
                    "YYYYMMDD"
                  )}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);

                  message.success("PDF exported successfully");
                } catch (error) {
                  console.error("Failed to download PDF:", error);
                  message.error(
                    error?.response?.data?.error || "Failed to export PDF"
                  );
                } finally {
                  setLoading(false);
                }
              }}
            >
              PDF
            </Button>
          </div>
        </Col>
        <Col span={3}>
          <div>
            <Text strong>Download Excel</Text>
            <Button
              icon={<FileExcelOutlined />}
              type="default"
              style={{ width: "100%", marginTop: 4 }}
              disabled={!selectedPartner || transactions.length === 0}
              onClick={async () => {
                try {
                  setLoading(true);
                  await exportToExcelWithAllocations();
                  message.success("Excel exported successfully");
                } catch (error) {
                  console.error("Failed to download Excel:", error);
                  message.error("Failed to export Excel");
                } finally {
                  setLoading(false);
                }
              }}
            >
              Excel
            </Button>
          </div>
        </Col>
        {/* Statistics Widgets */}
        {selectedPartner && transactions.length > 0 && (
          <Col span={24}>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Amount"
                    value={
                      isConverted
                        ? statistics.totalAmount * exchangeRate
                        : statistics.totalAmount
                    }
                    prefix={
                      isConverted ? (
                        <>
                          ${" "}
                          <SwapOutlined
                            style={{ fontSize: 12, marginLeft: 4 }}
                          />
                        </>
                      ) : (
                        "$"
                      )
                    }
                    suffix={displayCurrency}
                    precision={decimalPrecision}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Allocated"
                    value={
                      isConverted
                        ? statistics.totalAllocated * exchangeRate
                        : statistics.totalAllocated
                    }
                    prefix={
                      isConverted ? (
                        <>
                          ${" "}
                          <SwapOutlined
                            style={{ fontSize: 12, marginLeft: 4 }}
                          />
                        </>
                      ) : (
                        "$"
                      )
                    }
                    suffix={displayCurrency}
                    precision={decimalPrecision}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="Total Remaining"
                    value={
                      isConverted
                        ? statistics.totalRemaining * exchangeRate
                        : statistics.totalRemaining
                    }
                    prefix={
                      isConverted ? (
                        <>
                          ${" "}
                          <SwapOutlined
                            style={{ fontSize: 12, marginLeft: 4 }}
                          />
                        </>
                      ) : (
                        "$"
                      )
                    }
                    suffix={displayCurrency}
                    precision={decimalPrecision}
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        )}

        {/* AG Grid Table */}
        <Col span={24}>
          {!selectedPartner ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text>Please select a partner to view their transactions.</Text>
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text>No transactions found for this partner.</Text>
            </div>
          ) : (
            <div
              className="ag-theme-alpine"
              style={{ height: 700, width: "100%" }}
            >
              <AgGridWithColumnState
                key={`partner-transactions-${currencyRefreshKey}`}
                tableId="partner-transactions"
                gridApiRef={gridApiRef}
                columnDefs={columnDefs}
                rowData={transactions.flatMap((item) => {
                  const baseItem = {
                    ...item,
                    id: `${item.transaction_type || "invoice"}-${item.id}`,
                  };

                  // For transactions_first view, include allocations as separate rows
                  if (
                    sortBy === "transactions_first" &&
                    item.allocations?.length > 0
                  ) {
                    return [
                      { ...baseItem, isParent: true },
                      ...item.allocations.map((alloc, idx) => ({
                        ...alloc,
                        id: `alloc-${item.id}-${idx}`,
                        isAllocation: true,
                        parentId: item.id,
                      })),
                    ];
                  }
                  return baseItem;
                })}
                defaultColDef={defaultColDef}
                loading={loading}
                pagination={true}
                paginationPageSize={50}
                paginationPageSizeSelector={[25, 50, 100, 200]}
              />
            </div>
          )}
        </Col>
      </Row>

      <InvoiceViewModal
        visible={viewInvoiceVisible}
        onCancel={() => setViewInvoiceVisible(false)}
        invoiceId={viewInvoiceId}
        invoiceType={viewInvoiceType}
      />
    </Card>
  );
};

export default PartnerTransactions;
